type DownloadFileConfig = {
  fileName: string;
  fileType: 'attachment' | 'inline';
};

type ConfigType = Partial<DownloadFileConfig>;

/**
 * Downloads a file from the given URL.
 *
 * SAFARI FIX: Using iframe instead of <a> element to prevent "access control checks" error in Safari.
 * The issue occurs because Safari cancels pending fetch requests when it detects a navigation event,
 * which happens when using the <a> element click approach. This affects background notifications
 * that are fetched after downloading a file.
 */

/**
 * @param urlData The URL of the file to download
 * @param config Optional configuration for the download
 */
export const downloadFile = (urlData: string, config?: ConfigType) => {
  const url = new URL(urlData);

  if (config?.fileType) {
    url.searchParams.append('type', config.fileType);
  }

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  if (isSafari) {
    const iframe = document.createElement('iframe');

    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    // Use a timeout to ensure the iframe is ready before setting location
    setTimeout(() => {
      if (iframe.contentWindow) {
        // Clean up the iframe after download starts or if an error occurs
        iframe.onload = () => iframe.remove();
        iframe.onerror = () => iframe.remove();

        // Trigger the download without causing a "navigation intent" that would cancel fetches
        iframe.contentWindow.location.href = url.toString();
      }
    }, 0);
  } else {
    const link = document.createElement('a');
    const event = new MouseEvent('click');

    link.download = config?.fileName ?? 'file';
    link.href = url.toString();

    link.dispatchEvent(event);
    link.remove();
  }
};
